import { useCallback, useRef, useState, type ChangeEvent } from 'react';
import { useToast } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import {
  assignDocument,
  remindChecklistItem,
  requestChecklistItem,
  setChecklistItem,
  uploadWizardFile,
} from '../api';

export interface ChecklistTarget {
  id: string;
  title: string;
  documentTypeId: string | null;
}

/**
 * The per-item actions of Objekt anlegen 06/07: tick („geprüft"), „Anfordern", „Erinnern" and „Hochladen"
 * (the file goes through the wizard upload and is assigned the item's document type, which ticks the item).
 */
export function useChecklistActions(propertyId: string, onChanged: () => void) {
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const targetRef = useRef<ChecklistTarget | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const run = useCallback(
    async (id: string, work: () => Promise<unknown>, success: string) => {
      setBusyId(id);
      try {
        await work();
        toast({ message: success });
        onChanged();
      } catch (error) {
        toast({ message: errorMessage(error) });
      } finally {
        setBusyId(null);
      }
    },
    [onChanged, toast],
  );

  const toggle = useCallback(
    (item: ChecklistTarget, done: boolean) =>
      run(
        item.id,
        () => setChecklistItem(propertyId, item.id, done),
        done ? `${item.title} abgehakt.` : `${item.title} wieder offen.`,
      ),
    [propertyId, run],
  );

  const request = useCallback(
    (item: ChecklistTarget) =>
      run(
        item.id,
        () => requestChecklistItem(propertyId, item.id),
        `${item.title} beim Bauträger angefordert.`,
      ),
    [propertyId, run],
  );

  const remind = useCallback(
    (item: ChecklistTarget) =>
      run(item.id, () => remindChecklistItem(propertyId, item.id), 'Erinnerung an den Bauträger gesendet.'),
    [propertyId, run],
  );

  const startUpload = useCallback((item: ChecklistTarget) => {
    targetRef.current = item;
    inputRef.current?.click();
  }, []);

  const onFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      const item = targetRef.current;
      event.target.value = '';
      if (!file || !item) return;
      void run(
        item.id,
        async () => {
          const response = await uploadWizardFile(propertyId, file);
          const result = response.items[0];
          if (!result?.ok || !result.documentId) {
            throw new ApiError(400, {
              code: 'VALIDATION_FAILED',
              message: result?.error ?? 'Die Datei konnte nicht hochgeladen werden.',
            });
          }
          if (item.documentTypeId) await assignDocument(result.documentId, item.documentTypeId);
        },
        `${file.name} hochgeladen.`,
      );
    },
    [propertyId, run],
  );

  return { busyId, toggle, request, remind, startUpload, inputRef, onFileChange };
}
